import { useState, useEffect, useRef, useCallback } from "react";
import { API } from "@/App";
import { X, Send } from "lucide-react";

const SUGGESTIONS = [
  "¿Quién va ganando?",
  "¿Cómo van las apuestas?",
  "Explícame el último hoyo",
];

export const CadiiChat = ({ roundId }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    fetch(`${API}/v2/rounds/${roundId}/chat`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : []))
      .then(data => { setMessages(data.map(m => ({ role: m.role, content: m.content }))); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [open, loaded, roundId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/v2/rounds/${roundId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg }),
      });
      if (!res.ok || !res.body) throw new Error();
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const json = JSON.parse(payload);
            const chunk = json.delta || json.error || "";
            if (chunk) {
              setMessages(prev => {
                const next = [...prev];
                next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + chunk };
                return next;
              });
            }
          } catch { /* partial */ }
        }
      }
    } catch {
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", content: "Cadii no está disponible en este momento. Intenta de nuevo." };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  }, [input, streaming, roundId]);

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-4 z-50 w-16 h-16 rounded-full bg-[#103B31] shadow-lg shadow-black/20 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform overflow-hidden"
          data-testid="cadii-chat-fab"
          aria-label="Preguntar a Cadii"
        >
          <img src="/cadii/mascots/pencil_golfer_thumbsup.png" alt="Cadii" className="w-12 h-12 object-contain" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed inset-x-0 bottom-0 sm:inset-auto sm:bottom-20 sm:right-4 sm:w-[380px] z-50 flex flex-col bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl border border-[#DED5C2] max-h-[75vh] h-[520px]"
          style={{ fontFamily: "Manrope, sans-serif" }} data-testid="cadii-chat-panel">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#DED5C2] bg-white/60 flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-[#103B31] flex items-center justify-center overflow-hidden">
              <img src="/cadii/mascots/pencil_golfer_thumbsup.png" alt="Cadii" className="w-8 h-8 object-contain" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-[#102E32] text-sm">Cadii</p>
              <p className="text-[10px] text-[#103B31]">Tu caddie virtual · pregunta sobre la ronda</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#8A968C] hover:text-[#102E32] p-1" data-testid="cadii-chat-close">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center pt-6" data-testid="cadii-empty-state">
                <p className="text-sm text-[#6B7A6F] mb-4">Pregúntame sobre scores, apuestas o por qué alguien ganó un hoyo.</p>
                <div className="flex flex-col gap-2">
                  {SUGGESTIONS.map(s => (
                    <button key={s} onClick={() => send(s)}
                      className="text-xs text-[#103B31] border border-[#103B31]/30 rounded-full px-3 py-2 hover:bg-[#103B31]/5 transition-colors"
                      data-testid={`cadii-suggestion-${SUGGESTIONS.indexOf(s)}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#103B31] text-white rounded-br-md"
                    : "bg-[#F1EBDD] text-[#22302A] rounded-bl-md"
                }`} data-testid={`cadii-msg-${m.role}-${i}`}>
                  {m.content || (streaming && i === messages.length - 1 ? (
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 bg-[#F1EBDD]0 rounded-full animate-bounce" />
                      <span className="w-1.5 h-1.5 bg-[#F1EBDD]0 rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
                      <span className="w-1.5 h-1.5 bg-[#F1EBDD]0 rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
                    </span>
                  ) : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 px-3 py-3 border-t border-[#DED5C2] flex-shrink-0">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && send()}
              placeholder="Pregunta a Cadii..."
              disabled={streaming}
              className="flex-1 bg-[#F1EBDD] border border-[#E9E1CE] rounded-full px-4 py-2.5 text-sm text-[#102E32] placeholder:text-[#ADB8AE] focus:outline-none focus:ring-2 focus:ring-[#103B31]/30 disabled:opacity-50"
              data-testid="cadii-chat-input"
            />
            <button onClick={() => send()} disabled={streaming || !input.trim()}
              className="w-10 h-10 rounded-full bg-[#103B31] text-white flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform flex-shrink-0"
              data-testid="cadii-chat-send">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
